import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Calculator,
  PiggyBank,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Loader2,
  RefreshCw,
  ArrowUpCircle,
  ArrowDownCircle,
  Landmark,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DashboardLayout } from '../components/DashboardLayout';
import { useEntityStore } from '../store';
import { taxReservesAPI, bankAccountsAPI } from '../lib/api';
import { TaxReservesGroupSummary } from '../components/TaxReservesGroupSummary';
import { BROSTaxReservesContent } from './BROSTaxReservesPage';
import { PayrollTaxDebtsPanel } from '../components/PayrollTaxDebtsPanel';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';
import { format } from 'date-fns';

const taxTypeConfig = {
  vat_uk:      { label: 'UK VAT',              icon: Calculator, color: 'text-blue-600',   category: 'vat' },
  tva_fr:      { label: 'French TVA',          icon: Calculator, color: 'text-blue-600',   category: 'vat' },
  corp_tax_uk: { label: 'UK Corporation Tax',  icon: PiggyBank,  color: 'text-purple-600', category: 'corporate' },
  corp_tax_fr: { label: 'French IS',           icon: PiggyBank,  color: 'text-purple-600', category: 'corporate' },
  personal_uk: { label: 'UK Personal Tax',     icon: TrendingUp, color: 'text-orange-600', category: 'personal' },
  personal_fr: { label: 'French Income Tax',   icon: TrendingUp, color: 'text-orange-600', category: 'personal' },
};

const TAB_CATEGORIES = [
  { key: 'all',       label: 'All' },
  { key: 'vat',       label: 'VAT' },
  { key: 'corporate', label: 'Corporate' },
  { key: 'personal',  label: 'Personal' },
];

const formatCurrency = (value, currency = 'GBP') => {
  const symbols = { GBP: '£', EUR: '€' };
  return `${symbols[currency] || currency}${value.toLocaleString('en-GB', { minimumFractionDigits: 0 })}`;
};

// The grand-total stat cards sum required_amount/actual_balance across every
// reserve with no currency check. That sum is only ever meaningful when the
// reserves array happens to share one currency (the common case, since it's
// scoped to a single entity). When it doesn't (or there's nothing to check),
// this renders the raw number with a marker instead of guessing a symbol.
function MixedCurrencyTotal({ value, reason }) {
  const title = reason === 'empty'
    ? 'No reserves loaded for this entity; currency cannot be determined.'
    : 'Reserves span more than one currency; this total sums raw amounts with no single currency symbol.';
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="tax-reserves-mixed-currency-total">
      <span>{value.toLocaleString('en-GB', { minimumFractionDigits: 0 })}</span>
      <Badge
        variant="outline"
        data-testid="tax-reserves-mixed-currency-badge"
        className="text-[10px] px-1.5 py-0 h-4 border-amber-400 text-amber-700 dark:border-amber-600 dark:text-amber-400"
        title={title}
      >
        Mixed currencies
      </Badge>
    </span>
  );
}

function ReserveCard({ reserve }) {
  const config = taxTypeConfig[reserve.tax_type] || { label: reserve.tax_type, icon: Calculator };
  const Icon = config.icon;
  
  const percentage = reserve.required_amount > 0 
    ? Math.min(100, (reserve.actual_balance / reserve.required_amount) * 100)
    : 100;
  
  const getStatus = () => {
    if (percentage >= 100) return { label: 'Fully Funded', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle };
    if (percentage >= 80) return { label: 'On Track', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: AlertTriangle };
    return { label: 'Underfunded', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: AlertTriangle };
  };

  const status = getStatus();
  const StatusIcon = status.icon;
  const shortfall = Math.max(0, reserve.required_amount - reserve.actual_balance);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Card className="hover:shadow-lg transition-all">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={cn('p-2 rounded-lg bg-muted', config.color)}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold">{config.label}</h3>
                <p className="text-xs text-muted-foreground">{reserve.currency}</p>
              </div>
            </div>
            <Badge className={status.color}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {status.label}
            </Badge>
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Reserve Balance</span>
                <span className="font-mono font-medium">
                  {formatCurrency(reserve.actual_balance, reserve.currency)}
                </span>
              </div>
              <Progress value={percentage} className="h-3" />
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2">
              <div>
                <p className="text-xs text-muted-foreground">Required</p>
                <p className="font-mono font-medium">
                  {formatCurrency(reserve.required_amount, reserve.currency)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {shortfall > 0 ? 'Shortfall' : 'Surplus'}
                </p>
                <p className={cn(
                  'font-mono font-medium',
                  shortfall > 0 ? 'text-red-600' : 'text-green-600'
                )}>
                  {shortfall > 0 
                    ? `-${formatCurrency(shortfall, reserve.currency)}`
                    : `+${formatCurrency(reserve.actual_balance - reserve.required_amount, reserve.currency)}`
                  }
                </p>
              </div>
            </div>

            {shortfall > 0 && (
              <Button variant="outline" className="w-full" size="sm">
                Transfer to Reserve
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

const VAT_JURISDICTIONS = [
  { key: 'vat_uk', label: 'UK VAT', currency: 'GBP', flag: '🇬🇧' },
  { key: 'tva_fr', label: 'French TVA', currency: 'EUR', flag: '🇫🇷' },
];

// ── P&L-Based Reserve Estimator Panel ────────────────────────────────────────
// For non-BR entities, shows monthly estimated tax reserves derived from P&L
// data with editable rates. Server computes; this component only renders.

function EstimatorPanel({ entityId }) {
  const [data, setData] = useState(null);
  const [rates, setRates] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editingRates, setEditingRates] = useState(false);
  const [vatInput, setVatInput] = useState('');
  const [corpInput, setCorpInput] = useState('');

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [estRes, ratesRes] = await Promise.all([
        taxReservesAPI.getEstimated(entityId),
        taxReservesAPI.getRates(entityId),
      ]);
      setData(estRes.data);
      setRates(ratesRes.data);
      setVatInput(ratesRes.data.vat_rate_pct ?? '20');
      setCorpInput(ratesRes.data.corp_tax_rate_pct ?? '25');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (entityId && entityId !== 'all') {
      loadData();
    }
  }, [entityId]);

  const handleSaveRates = async () => {
    try {
      await taxReservesAPI.updateRates(entityId, {
        vat_rate_pct: vatInput,
        corp_tax_rate_pct: corpInput,
      });
      toast.success('Tax rates updated');
      setEditingRates(false);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (!entityId || entityId === 'all') return null;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.error) return null;

  const sym = data.currency === 'EUR' ? '\u20ac' : '\u00a3';
  const fmtMoney = (v) => {
    const num = parseFloat(v ?? '0');
    const abs = Math.abs(num);
    return `${num < 0 ? '-' : ''}${sym}${abs.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const months = data.months ?? [];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div className="space-y-4" data-testid="estimator-panel">
      {/* Header with source label */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-base font-semibold">Estimated Tax Reserves</h3>
          <Badge variant="outline" className="text-xs">Estimated from P&amp;L</Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEditingRates(!editingRates)}
          data-testid="edit-rates-btn"
        >
          {editingRates ? 'Cancel' : 'Edit Rates'}
        </Button>
      </div>

      {/* Editable rates card */}
      {editingRates && (
        <Card className="border-primary/30">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">VAT Rate (%)</label>
                <input
                  type="text"
                  value={vatInput}
                  onChange={(e) => setVatInput(e.target.value)}
                  className="w-full h-8 px-2 text-sm border rounded bg-background"
                  data-testid="vat-rate-input"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Corp Tax Rate (%)</label>
                <input
                  type="text"
                  value={corpInput}
                  onChange={(e) => setCorpInput(e.target.value)}
                  className="w-full h-8 px-2 text-sm border rounded bg-background"
                  data-testid="corp-rate-input"
                />
              </div>
              <Button size="sm" onClick={handleSaveRates} data-testid="save-rates-btn">
                Save & Recalculate
              </Button>
            </div>
            {rates?.is_default && (
              <p className="text-xs text-muted-foreground mt-2">
                Using default rates. Save to customize for this entity.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Current rates display (when not editing) */}
      {!editingRates && rates && (
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span>VAT: <span className="font-mono font-medium text-foreground">{rates.vat_rate_pct}%</span></span>
          <span>Corp Tax: <span className="font-mono font-medium text-foreground">{rates.corp_tax_rate_pct}%</span></span>
          {rates.is_default && <Badge variant="outline" className="text-xs">Default</Badge>}
        </div>
      )}

      {/* Totals summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Estimated VAT Reserve (YTD)</p>
            <p className="text-xl font-bold font-mono text-blue-600">
              {fmtMoney(data.totals?.estimated_vat_reserve)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Estimated Corp Tax Reserve (YTD)</p>
            <p className={cn(
              'text-xl font-bold font-mono',
              parseFloat(data.totals?.estimated_corp_tax_reserve ?? '0') < 0 ? 'text-red-600' : 'text-purple-600'
            )}>
              {fmtMoney(data.totals?.estimated_corp_tax_reserve)}
            </p>
            {parseFloat(data.totals?.estimated_corp_tax_reserve ?? '0') < 0 && (
              <p className="text-xs text-muted-foreground mt-1">Negative: loss position reduces reserve requirement</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly breakdown table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Monthly Breakdown ({data.year})</CardTitle>
          <CardDescription>Estimated from P&amp;L data</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th className="text-right">Revenue</th>
                  <th className="text-right">Costs</th>
                  <th className="text-right">Net P&amp;L</th>
                  <th className="text-right">Est. VAT</th>
                  <th className="text-right">Est. Corp Tax</th>
                </tr>
              </thead>
              <tbody>
                {months.map((m) => (
                  <tr key={m.month}>
                    <td className="font-medium text-sm">{monthNames[m.month - 1]}</td>
                    <td className="text-right font-mono text-sm">{fmtMoney(m.revenue)}</td>
                    <td className="text-right font-mono text-sm text-red-600">{fmtMoney(m.costs)}</td>
                    <td className={cn(
                      'text-right font-mono text-sm',
                      parseFloat(m.net_profit) < 0 ? 'text-red-600' : 'text-green-600'
                    )}>
                      {fmtMoney(m.net_profit)}
                    </td>
                    <td className="text-right font-mono text-sm text-blue-600">{fmtMoney(m.estimated_vat_reserve)}</td>
                    <td className={cn(
                      'text-right font-mono text-sm',
                      parseFloat(m.estimated_corp_tax_reserve) < 0 ? 'text-red-600' : 'text-purple-600'
                    )}>
                      {fmtMoney(m.estimated_corp_tax_reserve)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold bg-muted/50">
                  <td>Total</td>
                  <td className="text-right font-mono text-sm">
                    {fmtMoney(months.reduce((s, m) => s + parseFloat(m.revenue ?? '0'), 0).toString())}
                  </td>
                  <td className="text-right font-mono text-sm text-red-600">
                    {fmtMoney(months.reduce((s, m) => s + parseFloat(m.costs ?? '0'), 0).toString())}
                  </td>
                  <td className="text-right font-mono text-sm">
                    {fmtMoney(months.reduce((s, m) => s + parseFloat(m.net_profit ?? '0'), 0).toString())}
                  </td>
                  <td className="text-right font-mono text-sm text-blue-600">{fmtMoney(data.totals?.estimated_vat_reserve)}</td>
                  <td className={cn(
                    'text-right font-mono text-sm',
                    parseFloat(data.totals?.estimated_corp_tax_reserve ?? '0') < 0 ? 'text-red-600' : 'text-purple-600'
                  )}>
                    {fmtMoney(data.totals?.estimated_corp_tax_reserve)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const BR_ENTITY_ID = 'br_ltd';

export function TaxReservesPage() {
  const { selectedEntity, entities } = useEntityStore();
  const [reserves, setReserves] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [vatPosition, setVatPosition] = useState(null);
  const [sufficiency, setSufficiency] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    try {
      // Pass entity UUID — resolve_entity_filter handles both UUID and type code formats
      const entityId = selectedEntity !== 'all' ? selectedEntity : undefined;
      // Auto-sync VAT required_amount from invoices, then fetch everything
      await taxReservesAPI.syncVat();
      const [reservesRes, vatRes, accountsRes, suffRes] = await Promise.all([
        taxReservesAPI.getAll(entityId),
        taxReservesAPI.vatPosition(),
        bankAccountsAPI.getAll(),
        taxReservesAPI.sufficiency(entityId),
      ]);
      setReserves(reservesRes.data);
      setVatPosition(vatRes.data);
      setBankAccounts(accountsRes.data);
      setSufficiency(suffRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkAccount = async (reserveId, bankAccountId) => {
    try {
      await taxReservesAPI.linkAccount(reserveId, bankAccountId || null);
      await fetchData();
      toast.success(bankAccountId ? 'Bank account linked to reserve' : 'Bank account unlinked');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    // br_ltd renders BROSTaxReservesContent which fetches its own data.
    if (selectedEntity === BR_ENTITY_ID) return;
    if (entities.length > 0 || selectedEntity === 'all') {
      setIsLoading(true);
      fetchData();
    }
  }, [selectedEntity, entities]);

  const handleSyncVat = async () => {
    setIsLoading(true);
    try {
      await taxReservesAPI.syncVat();
      await fetchData();
      toast.success('Tax reserves synced from invoices');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const totalRequired = reserves.reduce((sum, r) => sum + r.required_amount, 0);
  const totalActual = reserves.reduce((sum, r) => sum + r.actual_balance, 0);
  const overallPercentage = totalRequired > 0 ? (totalActual / totalRequired) * 100 : 100;
  const underfundedCount = reserves.filter(r => r.actual_balance < r.required_amount * 0.8).length;

  // reserves is scoped to one entity, so it normally shares one currency;
  // only fall back to a marker when that assumption doesn't hold.
  const reserveCurrencies = Array.from(new Set(reserves.map((r) => r.currency).filter(Boolean)));
  const reservesCurrency = reserveCurrencies.length === 1 ? reserveCurrencies[0] : null;
  const reservesCurrencyReason = reserves.length === 0 ? 'empty' : 'mixed';

  // When the selected entity is Bonjour Residences Ltd, render the full
  // BROS Tax Reserves page (two-tab: Overview + Controls). One component, two routes.
  const isBREntity = selectedEntity === BR_ENTITY_ID;

  // BR entity: render the same two-tab BROS page used by the BR OS scope route.
  if (isBREntity) {
    return (
      <DashboardLayout>
        <div data-testid="tax-reserves-page">
          <BROSTaxReservesContent />
        </div>
      </DashboardLayout>
    );
  }

  // No entity selected: prompt the user to pick one.
  if (selectedEntity === 'all') {
    return (
      <DashboardLayout>
        <div className="flex flex-col gap-8" data-testid="tax-reserves-page">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Tax Reserves</h1>
            <p className="text-muted-foreground mt-1">VAT, Corporation Tax, and Personal Tax budgets</p>
          </div>
          {/* All entities: group aggregate, jurisdiction then entity. Picking a
              single entity in the sidebar still drills into it. */}
          <TaxReservesGroupSummary />
          <PayrollTaxDebtsPanel show="debts" />
        </div>
      </DashboardLayout>
    );
  }

  // Non-BR entity: generic reserves view with estimator.
  return (
    <DashboardLayout>
      <div className="flex flex-col gap-8" data-testid="tax-reserves-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Tax Reserves</h1>
            <p className="text-muted-foreground mt-1">VAT, Corporation Tax, and Personal Tax budgets</p>
          </div>
          <Button onClick={handleSyncVat} data-testid="sync-tax-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Sync VAT Position
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Calculator className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Required</p>
                  <p className="text-xl font-bold">
                    {reservesCurrency
                      ? formatCurrency(totalRequired, reservesCurrency)
                      : <MixedCurrencyTotal value={totalRequired} reason={reservesCurrencyReason} />}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                  <PiggyBank className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Reserved</p>
                  <p className="text-xl font-bold text-green-600">
                    {reservesCurrency
                      ? formatCurrency(totalActual, reservesCurrency)
                      : <MixedCurrencyTotal value={totalActual} reason={reservesCurrencyReason} />}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Funding Level</p>
                  <p className="text-xl font-bold">{overallPercentage.toFixed(0)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Attention Needed</p>
                  <p className="text-xl font-bold text-red-600">{underfundedCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sufficiency Indicator */}
        {sufficiency && sufficiency.shortfall_count > 0 && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Reserve Shortfall: {sufficiency.shortfall_count} reserve{sufficiency.shortfall_count !== 1 ? 's' : ''} underfunded
                </p>
                <div className="mt-2 space-y-1.5">
                  {sufficiency.items.filter(i => i.status === 'shortfall').map(item => {
                    const cfg = taxTypeConfig[item.tax_type] || { label: item.tax_type };
                    return (
                      <div key={item.reserve_id || item.tax_type} className="flex items-center justify-between text-sm text-red-700 dark:text-red-300">
                        <span>{cfg.label} ({item.entity_id})</span>
                        <span className="font-mono">
                          {formatCurrency(parseFloat(item.held))} / {formatCurrency(parseFloat(item.required))}
                          {' '}({item.pct_funded}% funded, shortfall {formatCurrency(parseFloat(item.shortfall))})
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                  Overall: {sufficiency.overall_pct_funded}% funded
                  (reserved {formatCurrency(parseFloat(sufficiency.total_held))} of {formatCurrency(parseFloat(sufficiency.total_required))})
                </p>
              </div>
            </div>
          </div>
        )}
        {sufficiency && sufficiency.shortfall_count === 0 && (
          <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 p-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm text-green-700 dark:text-green-300">
                All reserves fully funded ({sufficiency.overall_pct_funded}%)
              </span>
            </div>
          </div>
        )}

        {/* Reserve Cards */}
        <div className="pt-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : reserves.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calculator className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Tax Reserves</h3>
              <p className="text-muted-foreground mb-4">
                No tax reserves configured yet. Sync VAT position from invoices to get started.
              </p>
              <Button onClick={handleSyncVat}>
                Sync VAT Position
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="all">
            <TabsList>
              {TAB_CATEGORIES.map((tab) => {
                const filtered = tab.key === 'all'
                  ? reserves
                  : reserves.filter((r) => (taxTypeConfig[r.tax_type]?.category ?? 'other') === tab.key);
                return (
                  <TabsTrigger key={tab.key} value={tab.key}>
                    {tab.label}
                    <span className="ml-1.5 text-xs opacity-60">({filtered.length})</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {/* All tab — grouped by category */}
            <TabsContent value="all" className="mt-4 space-y-8">
              {TAB_CATEGORIES.filter((t) => t.key !== 'all').map((tab) => {
                const group = reserves.filter((r) => (taxTypeConfig[r.tax_type]?.category ?? 'other') === tab.key);
                if (group.length === 0) return null;
                return (
                  <div key={tab.key}>
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                      {tab.label}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {group.map((reserve, index) => (
                        <ReserveCard key={reserve.id || index} reserve={reserve} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </TabsContent>

            {/* VAT tab — jurisdiction sub-tabs */}
            <TabsContent value="vat" className="mt-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Only paid invoices are counted. Output VAT is collected from clients; input VAT is reclaimable from suppliers.
              </p>

              <Tabs defaultValue={VAT_JURISDICTIONS[0].key}>
                <TabsList>
                  {VAT_JURISDICTIONS.map((jur) => {
                    const pos = vatPosition?.[jur.key];
                    const count = pos?.invoices?.length ?? 0;
                    return (
                      <TabsTrigger key={jur.key} value={jur.key}>
                        {jur.flag} {jur.label}
                        <span className="ml-1.5 text-xs opacity-60">({count})</span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {VAT_JURISDICTIONS.map((jur) => {
                  const pos = vatPosition?.[jur.key];
                  if (!pos) return <TabsContent key={jur.key} value={jur.key} />;
                  const invoices = pos.invoices ?? [];
                  const outbound = invoices.filter((i) => i._vat_direction === 'output');
                  const inbound = invoices.filter((i) => i._vat_direction === 'input');
                  const sym = jur.currency === 'EUR' ? '€' : '£';
                  const fmt = (v) => `${sym}${Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
                  const vatReserve = reserves.find((r) => r.tax_type === jur.key);

                  return (
                    <TabsContent key={jur.key} value={jur.key} className="mt-4 space-y-4">
                      {/* Summary cards */}
                      <div className="grid grid-cols-3 gap-3">
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-1">
                              <ArrowUpCircle className="h-4 w-4 text-green-500" />
                              <p className="text-xs text-muted-foreground">Output VAT collected</p>
                            </div>
                            <p className="text-xl font-bold font-mono text-green-600">{fmt(pos.output_vat)}</p>
                            <p className="text-xs text-muted-foreground/60 mt-0.5">{outbound.length} paid client invoices</p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-1">
                              <ArrowDownCircle className="h-4 w-4 text-blue-500" />
                              <p className="text-xs text-muted-foreground">Input VAT reclaimable</p>
                            </div>
                            <p className="text-xl font-bold font-mono text-blue-600">{fmt(pos.input_vat)}</p>
                            <p className="text-xs text-muted-foreground/60 mt-0.5">{inbound.length} paid supplier invoices</p>
                          </CardContent>
                        </Card>
                        <Card className={pos.net_vat > 0 ? 'border-red-200 dark:border-red-900' : 'border-green-200 dark:border-green-900'}>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-1">
                              <Calculator className="h-4 w-4 text-muted-foreground" />
                              <p className="text-xs text-muted-foreground">Net VAT due</p>
                            </div>
                            <p className={cn('text-xl font-bold font-mono', pos.net_vat > 0 ? 'text-red-600' : 'text-green-600')}>
                              {pos.net_vat >= 0 ? '' : '−'}{fmt(pos.net_vat)}
                            </p>
                            <p className="text-xs text-muted-foreground/60 mt-0.5">output − input</p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Reserve bank account */}
                      {vatReserve && (
                        <div className="space-y-2">
                          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">VAT Reserve Account</h4>
                          <Card>
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                  <div className="p-2 rounded-lg bg-muted">
                                    <Landmark className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                  <div>
                                    {vatReserve.bank_account_id ? (
                                      <>
                                        <p className="text-sm font-medium">
                                          {bankAccounts.find((a) => a.id === vatReserve.bank_account_id)?.account_name ?? 'Linked account'}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          {bankAccounts.find((a) => a.id === vatReserve.bank_account_id)?.bank_name}
                                          {' · '}
                                          <span className={cn('font-mono font-semibold', vatReserve.actual_balance >= vatReserve.required_amount ? 'text-green-600' : 'text-red-600')}>
                                            {formatCurrency(vatReserve.actual_balance, vatReserve.currency)}
                                          </span>
                                          {vatReserve.required_amount > 0 && (
                                            <span className="ml-1 text-muted-foreground/60">
                                              / {formatCurrency(vatReserve.required_amount, vatReserve.currency)} required
                                            </span>
                                          )}
                                        </p>
                                      </>
                                    ) : (
                                      <p className="text-sm text-muted-foreground">No bank account linked</p>
                                    )}
                                  </div>
                                </div>
                                <Select
                                  value={vatReserve.bank_account_id ?? ''}
                                  onValueChange={(val) => handleLinkAccount(vatReserve.id, val || null)}
                                >
                                  <SelectTrigger className="w-[200px] h-8 text-xs">
                                    <SelectValue placeholder="Link bank account…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="">Unlink</SelectItem>
                                    {bankAccounts
                                      .filter((a) => a.currency === vatReserve.currency)
                                      .map((acc) => (
                                        <SelectItem key={acc.id} value={acc.id}>
                                          {acc.account_name} · {acc.bank_name}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              {vatReserve.bank_account_id && (
                                <div className="mt-3">
                                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                                    <span>Funded</span>
                                    <span>{vatReserve.required_amount > 0 ? Math.min(100, (vatReserve.actual_balance / vatReserve.required_amount) * 100).toFixed(0) : 100}%</span>
                                  </div>
                                  <Progress
                                    value={vatReserve.required_amount > 0 ? Math.min(100, (vatReserve.actual_balance / vatReserve.required_amount) * 100) : 100}
                                    className="h-2"
                                  />
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </div>
                      )}

                      {/* Invoice table */}
                      {invoices.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Paid Invoices</h4>
                          <Card>
                            <CardContent className="p-0">
                              <div className="overflow-x-auto">
                                <table className="data-table">
                                  <thead>
                                    <tr>
                                      <th>Invoice #</th>
                                      <th>Supplier / Client</th>
                                      <th>Type</th>
                                      <th>Date</th>
                                      <th className="text-right">Amount</th>
                                      <th className="text-right">VAT rate</th>
                                      <th className="text-right">VAT amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {invoices.map((inv) => (
                                      <tr key={inv.id}>
                                        <td className="font-mono text-sm">{inv.invoice_number}</td>
                                        <td className="font-medium">{inv.supplier_client_name}</td>
                                        <td>
                                          <span className={cn(
                                            'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
                                            inv._vat_direction === 'output'
                                              ? 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/20 dark:text-green-400'
                                              : 'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-900/20 dark:text-blue-400'
                                          )}>
                                            {inv._vat_direction === 'output' ? 'Client' : 'Supplier'}
                                          </span>
                                        </td>
                                        <td className="text-muted-foreground whitespace-nowrap">
                                          {inv.date ? format(new Date(inv.date), 'dd MMM yyyy') : '—'}
                                        </td>
                                        <td className="text-right font-mono">{sym}{(inv.amount ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                                        <td className="text-right text-muted-foreground">{inv.vat_rate ?? 0}%</td>
                                        <td className={cn(
                                          'text-right font-mono font-semibold',
                                          inv._vat_direction === 'output' ? 'text-green-600' : 'text-blue-600'
                                        )}>
                                          {sym}{(inv.vat_amount ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      )}
                    </TabsContent>
                  );
                })}
              </Tabs>
            </TabsContent>

            {/* Corporate tab */}
            {TAB_CATEGORIES.filter((t) => t.key === 'corporate' || t.key === 'personal').map((tab) => {
              const filtered = reserves.filter((r) => (taxTypeConfig[r.tax_type]?.category ?? 'other') === tab.key);
              return (
                <TabsContent key={tab.key} value={tab.key} className="mt-4">
                  {filtered.length === 0 ? (
                    <Card>
                      <CardContent className="py-8 text-center text-muted-foreground">
                        No {tab.label} reserves found.
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filtered.map((reserve, index) => (
                        <ReserveCard key={reserve.id || index} reserve={reserve} />
                      ))}
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        )}
        </div>

        <PayrollTaxDebtsPanel entityId={selectedEntity} show="debts" />

        {/* P&L-based estimator */}
        <EstimatorPanel entityId={selectedEntity} />

        {/* Info Card */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-primary/10">
                <AlertTriangle className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Tax Reserve Recommendations</h3>
                <p className="text-sm text-muted-foreground">
                  We recommend maintaining tax reserves at 100% of estimated liability. When client payments are received,
                  consider transferring the VAT portion immediately to your reserve account to avoid cash flow issues at
                  quarter end.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
